<template>
  <div>
    <vxe-toolbar class="mt-2">
      <template #tools>
        <a-checkbox v-if="dataGroup.length > 1" :checked="isShowSubtotal" class="mr-2" @change="handleShowChange">{{ $t('bill.show_subtotal') }}</a-checkbox>
        <a-checkbox class="mr-2" :checked="showZeroBills" @change="(e) => setShowZeroBills(e.target.checked)">{{ $t('bill.contain_amount_0') }}</a-checkbox>
        <a-button @click="download"><icon type="download" /></a-button>
      </template>
    </vxe-toolbar>
    <vxe-grid
      class="w-100 mt-2"
      border
      max-height="800"
      ref="analysisTable"
      :columns="tableData.columns"
      :data="tableDataList"
      show-header-overflow
      show-footer-overflow
      show-overflow
      auto-resize
      :loading="loading"
      :span-method="rowspanMethod"
      :cell-style="cellStyle"
      :cell-class-name="cellClassName"
      :footer-cell-class-name="cellClassName"
      :footer-cell-style="footerCellStyle"
      :pager-config="tablePage"
      :show-footer="tableDataList.length"
      :footer-method="footerMethod"
      :sort-config="{sortMethod: sortMethod}"
      :scroll-x="{gt: 100}"
      @page-change="handlePageChange"
      @cell-click="handleCellClick"
      @footer-cell-click="handleFooterCellClick" />
  </div>
</template>

<script>
import XLSX from 'xlsx'
import * as R from 'ramda'
import { mapState, mapMutations } from 'vuex'
import currencyRequest from '@Bill/mixins/currencyRequest'
import { ANALYSIS_GROUPT_TYPE_MAP } from '@Bill/constants'
import { addDataToSheetAfterFormat } from '@/utils/xlsx'
import { numerify } from '@/filters'
import AnalysisesMixin from '../mixins/analysises'

const flattenData = (data, depth = 1, billTypes) => {
  const result = []

  // 使用用户提供的 transformDataASimple，将任意嵌套结构按 date_info 汇总为扁平数组
  function transformDataASimple (dataA) {
    const resultMap = new Map()

    function extractAndMerge (data) {
      if (!data) return

      if (Array.isArray(data)) {
        data.forEach(extractAndMerge)
      } else if (typeof data === 'object') {
        if (data.date_info) {
          const dateKey = data.date_info
          if (!resultMap.has(dateKey)) {
            resultMap.set(dateKey, {})
          }
          const target = resultMap.get(dateKey)

          Object.keys(data).forEach(key => {
            if (key === 'date_info') {
              target[key] = data[key]
            } else {
              const value = data[key]
              const numValue = parseFloat(value)
              const isNumeric = !isNaN(numValue) &&
                value !== null &&
                value !== '' &&
                typeof value !== 'boolean'

              if (isNumeric) {
                target[key] = (target[key] || 0) + numValue
              } else if (target[key] === undefined || target[key] === '') {
                target[key] = value
              }
            }
          })
        }

        Object.keys(data).forEach(key => {
          if (data[key] && typeof data[key] === 'object') {
            extractAndMerge(data[key])
          }
        })
      }
    }

    extractAndMerge(dataA)
    return Array.from(resultMap.values())
      .sort((a, b) => String(a.date_info).localeCompare(String(b.date_info)))
  }

  function traverse (node, currentNames, currentDepth) {
    if (R.is(Array, node.data)) {
      if (currentDepth === depth) {
        // 到达目标深度，先把 node.data 处理成按 date_info 汇总的一维数组
        let processedData = node.data
        processedData = transformDataASimple(processedData)

        const entry = {}
        for (let i = 1; i <= currentDepth; i++) {
          entry[`name${i}`] = currentNames[i - 1]
        }
        const total = {}
        processedData.forEach(item => {
          const key = item.date_info
          billTypes.forEach(billType => {
            entry[key] = { ...(entry[key] || {}), [billType]: item[billType] }
            total[billType] = (total[billType] || 0) + (item[billType] || 0)
          })
        })
        entry.all = total
        result.push(entry)
      } else {
        // 继续深入下一层
        node.data.forEach(child => {
          traverse(child, [...currentNames, child.name], currentDepth + 1)
        })
      }
    }
  }

  data.forEach(item => {
    traverse(item, [item.name], 1)
  })
  return result
}

export default {
  name: 'AnalysisesTable',
  components: {
  },
  mixins: [currencyRequest, AnalysisesMixin],
  props: {
    formData: {
      type: Object,
      require: true,
    },
    dataList: {
      type: Array,
      require: true,
    },
    originDataList: {
      type: Array,
      require: true,
    },
    tagGroupParams: Object,
    projectTagGroupParams: Object,
    instanceTagGroupParams: Object,
    isGoPage: Boolean,
    billType: {
      type: Array,
    },
    dataGroup: {
      type: Array,
      default: () => ([]),
    },
    loading: Boolean,
    isShowSubtotal: Boolean,
  },
  computed: {
    ...mapState('bill', {
      showZeroBills: state => state.showZeroBills,
    }),
    sortData () {
      return this.getSortData(this.tableData.data)
    },
    tableDataList () {
      const { currentPage, pageSize } = this.tablePage
      return this.sortData.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    },
    billTypes () {
      if (this.formData.show_bill_type === 'show_bill_usage') {
        return ['aggregate_usage', ...this.billType]
      }
      return this.billType
    },
    tableData () {
      const groupLen = this.dataGroup.length
      // const truthGroup = this.dataGroup.filter(item => item !== 'aggregate_usage_unit')
      const dataList = flattenData(this.originDataList || [], groupLen, this.billTypes)
      let data = dataList
      const initFeeData = {}
      const billTypes = this.billTypes || []
      if (!billTypes.length) {
        return { columns: [], data: [] }
      }
      billTypes.forEach(type => {
        initFeeData[type] = 0
      })
      // 整理表头
      const columns = this.dataGroup.map((group, idx) => {
        const groupInfo = this.getGroupInfo(group)
        let title = ['tag', 'instance_tag', 'project_tag'].includes(groupInfo.type) ? this.getTagKey(groupInfo.value) : (ANALYSIS_GROUPT_TYPE_MAP[groupInfo.type]?.value || this.$t('bill.text_72'))
        // 累计用量单位
        if (group === 'aggregate_usage_unit') {
          title = this.$t('meter.aggregate_usage_unit')
          return {
            field: `name${idx + 1}`,
            minWidth: 150,
            fixed: 'left',
            resizable: true,
            title,
            sortable: this.dataGroup.length === 1,
            sortBy: `name${idx + 1}`,
            formatter: ({ row }) => {
              return this.getNameI18n(row[`name${idx + 1}`], groupInfo.type)
            },
          }
        }
        return {
          field: `name${idx + 1}`,
          minWidth: 150,
          fixed: 'left',
          resizable: true,
          title,
          sortable: this.dataGroup.length === 1,
          sortBy: `name${idx + 1}`,
          formatter: ({ row }) => {
            if (row.type === 'subtotal' && ['tag', 'instance_tag', 'project_tag'].includes(groupInfo.type) && row.subTotalNameList && !row.subTotalNameList.includes(`name${idx + 1}`)) {
              return row[`name${idx + 1}`]
            }
            return this.getNameI18n(row[`name${idx + 1}`], groupInfo.type)
          },
        }
      })
      if (data[0]) {
        const keys = Object.keys(data[0])
        keys.map(date => {
          if (columns && !columns.some(item => item.field === date) && date !== 'all') {
            const col = {
              field: date,
              title: date,
              minWidth: 135,
              resizable: true,
              sortable: this.dataGroup.length === 1,
              sortBy: date,
              formatter: ({ row }) => {
                return row[date] && row[date][billTypes[0]] ? `${this.currencySign} ${numerify(row[date][billTypes[0]], '0,0.00')}` : `${this.currencySign} 0.00`
              },
            }
            if (billTypes.length > 1) {
              col.children = []
              delete col.formatter
              delete col.minWidth
              delete col.resizable
              delete col.sortable
              delete col.sortBy
              billTypes.forEach(type => {
                col.children.push({
                  field: `${date}->${type}`,
                  title: this.$t(`bill.bill_type.${type}`),
                  minWidth: 135,
                  resizable: true,
                  sortable: this.dataGroup.length === 1,
                  sortBy: `${date}->${type}`,
                  align: 'right',
                  formatter: ({ row }) => {
                    if (type === 'aggregate_usage') {
                      if (row.rowType === 'total') {
                        return '-'
                      }
                      return numerify((row[date] && row[date][type]) || 0, '0.00')
                    }
                    if (row[date] && row[date][type]) {
                      return `${this.currencySign} ${numerify(row[date][type], '0,0.00')}`
                    }
                    return `${this.currencySign} 0.00`
                  },
                })
              })
            }
            columns.push(col)
          }
        })
      }
      const all_fee = { ...initFeeData }
      dataList.forEach(item => {
        billTypes.forEach(type => {
          all_fee[type] += item.all[type]
        })
      })
      // 计算每行的占比
      data.forEach(item => {
        item.percent = {}
        billTypes.forEach(type => {
          item.percent[type] = all_fee[type] > 0 ? (parseFloat(item.all[type] / all_fee[type]) * 100).toFixed(2) + '%' : '0%'
        })
      })
      function subData (data, nameIdx, vm) {
        const keys = Object.keys(data[0])
        const result = R.clone({ ...(data[0]), type: 'subtotal', subTotalNameList: Array(Math.max(nameIdx - 1, 0)).fill().map((_, i) => `name${i + 1}`) })
        result[`name${nameIdx}`] = vm.$t('bill.subtotal')
        if (nameIdx < 5) {
          for (let i = nameIdx; i < 5; i++) {
            result[`name${i + 1}`] = ''
          }
        }
        data.slice(1).map(d => {
          keys.map(k => {
            if (d.type !== 'subtotal') {
              if (R.is(Object, d[k])) {
                const valueKeys = Object.keys(d[k])
                valueKeys.map(valueKey => {
                  if (R.is(Number, d[k][valueKey]) && R.is(Number, result[k][valueKey])) {
                    result[k][valueKey] += d[k][valueKey]
                  }
                })
              }
            }
          })
        })
        return result
      }
      function addSubtotal (data, groupByLen = 1, vm) {
        // 需要增加小计次数
        const subList = Array(Math.max(groupByLen - 1, 0)).fill().map((_, i) => {
          return {
            start: 0,
            checkList: Array(i + 1).fill().map((_, j) => `name${j + 1}`),
          }
        })
        subList.reverse()
        let result = [...data]
        let tempList = []
        subList.forEach(sub => {
          let start = 0
          for (let i = 0; i < result.length; i++) {
            const cur = result[i]
            const prev = result[i - 1] ? result[i - 1] : null
            if (prev) {
              if (!sub.checkList.every(name => cur[name] === prev[name])) {
                const target = result.slice(start, i)
                if (target.length) {
                  tempList.push(subData(target, sub.checkList.length + 1, vm))
                  start = i
                }
              }
            }
            tempList.push(cur)
            if (i === result.length - 1) {
              const target = result.slice(start)
              if (target.length) {
                tempList.push(subData(target, sub.checkList.length + 1, vm))
              }
            }
          }
          result = tempList
          tempList = []
        })
        return result
      }
      if (this.isShowSubtotal) {
        data = addSubtotal(data, groupLen, this)
      }
      // 计算总条数
      // eslint-disable-next-line
      this.tablePage.total = data.length
      // eslint-disable-next-line
      this.tablePage.currentPage = 1

      // 添加总计和占比
      if (billTypes.length > 1) {
        const allCol = {
          field: 'all',
          title: this.$t('bill.text_316'),
          fixed: 'right',
          align: 'center',
          children: [],
        }
        const percentCol = {
          field: 'percent',
          title: this.$t('bill.text_317'),
          fixed: 'right',
          align: 'center',
          children: [],
        }
        billTypes.forEach(type => {
          allCol.children.push({
            field: `all->${type}`,
            title: this.$t(`bill.bill_type.${type}`),
            minWidth: 115,
            resizable: true,
            sortable: this.groupLen === 1,
            sortBy: 'all',
            align: 'right',
            formatter: ({ row }) => {
              if (type === 'aggregate_usage') {
                if (row.rowType === 'total') {
                  return '-'
                }
                return numerify(row.all[type] || 0, '0.00')
              }
              return `${this.currencySign} ${numerify(row.all[type], '0,0.00')}`
            },
          })
          if (type !== 'aggregate_usage') {
            percentCol.children.push({
              field: `all->${type}`,
              title: this.$t(`bill.bill_type.${type}`),
              minWidth: 115,
              resizable: true,
              sortable: this.groupLen === 1,
              sortBy: 'all',
              align: 'right',
              formatter: ({ row }) => {
                if (type === 'aggregate_usage') {
                  return '-'
                }
                return row.percent[type]
              },
            })
          }
        })
        columns.push(allCol, percentCol)
      } else {
        const type = billTypes[0]
        columns.push({
          field: 'all',
          title: this.$t('bill.text_316'),
          minWidth: 115,
          resizable: true,
          sortable: this.groupLen === 1,
          sortBy: 'all',
          align: 'center',
          fixed: 'right',
          formatter: ({ row }) => {
            return `${this.currencySign} ${numerify(row.all[type], '0,0.00')}`
          },
        })
        columns.push({
          field: 'percent',
          title: this.$t('bill.text_317'),
          minWidth: 115,
          resizable: true,
          sortable: this.groupLen === 1,
          sortBy: 'all',
          align: 'center',
          fixed: 'right',
          formatter: ({ row }) => {
            return row.percent[type]
          },
        })
      }
      // 按第一维度名称排序
      const sorter = R.sortBy(R.prop('name'))
      const result = sorter(data)
      return {
        columns,
        data: result,
      }
    },
    footerRow () {
      const rows = []
      // footerRow中的一行
      const { data, columns } = this.tableData
      const totalRow = []
      columns.forEach(col => {
        if (col.field === 'name1') {
          totalRow.push(this.$t('bill.text_316'))
        } else if (col.field.startsWith('name')) {
          totalRow.push('')
        }
      })
      columns.map(col => {
        const { field } = col
        if (['name1', 'name2', 'name3', 'name4', 'name5', 'percent'].indexOf(field) === -1) {
          const total = { stat_value: 0, stat_value_sec: 0, gross_value: 0, paid_value: 0, discount_value: 0, aggregate_usage: 0 }
          data.map(item => {
            if (item.rowType !== 'total' && item.type !== 'subtotal') {
              this.billTypes.forEach(type => {
                total[type] += isNaN(item[field] && item[field][type]) ? 0 : item[field][type]
              })
            }
          })
          this.billTypes.forEach(type => {
            if (type === 'aggregate_usage') {
              totalRow.push('-')
            } else {
              totalRow.push(`${this.currencySign} ${numerify(total[type], '0,0.00')}`)
            }
          })
        }
      })
      this.billTypes.forEach(type => {
        if (type === 'aggregate_usage') {
          totalRow.push('-')
        } else {
          totalRow.push('100%')
        }
      })
      rows.push(totalRow)
      return rows
    },
    exportParams () {
      const ret = []
      const mergeList = []
      // 生成导出表头
      const { columns, data } = this.tableData
      const dataColumns = []
      if (this.billTypes.length === 1) {
        // 单行表头
        const titleRow = columns.map(item => {
          dataColumns.push(item)
          return item.title
        })
        ret.push(titleRow)
      } else if (this.billTypes.length > 1) {
        // 两行表头
        const titleRow1 = []
        const titleRow2 = []
        columns.map(item => {
          if (item.children) {
            // 表头合并
            mergeList.push({
              s: {
                r: 0,
                c: dataColumns.length,
              },
              e: {
                r: 0,
                c: dataColumns.length - 1 + item.children.length,
              },
            })
            item.children.map(child => {
              dataColumns.push(child)
              titleRow1.push(item.title)
              titleRow2.push(child.title)
            })
          } else {
            dataColumns.push(item)
            titleRow1.push(item.title)
            titleRow2.push(item.title)
          }
        })
        ret.push(titleRow1)
        ret.push(titleRow2)
      }
      // 生成数据
      data.map(item => {
        const row = []
        dataColumns.map(col => {
          if (col.formatter) {
            row.push(col.formatter({ row: item }))
          } else {
            row.push(item[col.field])
          }
        })
        ret.push(row)
      })
      // 增加总计行
      this.footerRow.map(item => {
        ret.push(item)
      })
      // 生成合并单元格规则
      const nameCols = columns.filter(item => item.field.startsWith('name'))
      const cols = nameCols.slice(0, nameCols.length - 1)
      cols.forEach((_, colIdx) => {
        let start = 0
        let end = 0
        ret.map((item, index) => {
          const idList = Array(colIdx + 1).fill()
          const curId = idList.map((_, i) => item[i]).join('-')
          const nextId = ret[index + 1] ? idList.map((_, i) => ret[index + 1][i]).join('-') : null
          if (nextId) {
            if (curId === nextId) {
              end = index + 1
            } else {
              if (start !== end) {
                mergeList.push({ s: { r: start, c: colIdx }, e: { r: end, c: colIdx } })
              }
              start = index + 1
              end = index + 1
            }
          } else {
            end = index
            mergeList.push({ s: { r: start, c: 0 }, e: { r: end, c: cols.length } })
          }
        })
      })
      return {
        data: ret,
        merges: mergeList,
      }
    },
  },
  methods: {
    ...mapMutations('bill', {
      setShowZeroBills: 'UPDATE_ZERO_BILL_SHOW',
    }),
    handleShowChange (val) {
      this.$emit('showChange', val.target.checked)
    },
    handlePageChange (params) {
      const { $event, currentPage, pageSize } = params
      const { type } = $event
      if (type === 'size') {
        this.tablePage.pageSize = pageSize
        this.tablePage.currentPage = 1
      }
      if (type === 'current') {
        this.tablePage.currentPage = currentPage
      }
    },
    footerMethod () {
      return this.footerRow
    },
    handleCellClick ({ row, column }) {
      if (!this.isGoPage) return
      let { property = '' } = column
      const { type = '' } = row
      if (property.includes('->')) {
        property = property.split('->')[0]
      }
      if (['percent'].includes(property) || property.startsWith('name')) return
      const ret = {}
      // 非维度列和总计占比列的数据列，使用数据列的时间
      if (!(['name1', 'name2', 'name3', 'name4', 'name5', 'percent'].includes(property) || ['all'].includes(property))) {
        const { data_type } = this.formData.date
        if (data_type === 'day') {
          ret.start_date = this.$moment(property).format('YYYY-MM-DD') + 'TZ'
          ret.end_date = ret.start_date
        } else {
          ret.start_date = this.$moment(property).startOf('month').format('YYYY-MM-DD') + 'TZ'
          ret.end_date = this.$moment(property).endOf('month').format('YYYY-MM-DD') + 'TZ'
        }
      }
      this.dataGroup.map((group, index) => {
        if (type === 'subtotal' && !row.subTotalNameList.includes(`name${index + 1}`)) return
        let groupKey = group
        let groupValue = ''
        if (groupKey.startsWith('tag:')) {
          groupKey = 'tag'
          groupValue = group.replace('tag:', '')
        } else if (groupKey.startsWith('project_tag:')) {
          groupKey = 'project_tag'
          groupValue = group.replace('project_tag:', '')
        } else if (groupKey.startsWith('instance_tag:')) {
          groupKey = 'instance_tag'
          groupValue = group.replace('instance_tag:', '')
        }
        if (['tag', 'instance_tag', 'project_tag'].includes(groupKey)) {
          if (!ret[groupKey]) ret[groupKey] = {}
          if (ret[groupKey[groupValue]]) {
            ret[groupKey][groupValue] = [...ret[groupKey][groupValue], row[`name${index + 1}`]]
          } else {
            ret[groupKey][groupValue] = [row[`name${index + 1}`]]
          }
        } else {
          ret[groupKey] = row[`name${index + 1}`]
        }
      })
      // 去除云订阅id后缀
      if (ret.cloudprovider_name) {
        const idx = this.dataGroup.findIndex(item => item === 'cloudprovider_name')
        if (idx !== -1) {
          const cloudprovider_id = this.findCloudproviderId(this.originDataList, ret.cloudprovider_name, idx)
          if (cloudprovider_id) {
            ret.cloudprovider_id = cloudprovider_id
          }
        }
      }
      this.$emit('goBillPage', ret)
    },
    findCloudproviderId (list, name, idx) {
      // 找到数据的idx层，然后返回name相同的数据的id
      if (idx === 0) {
        const target = list.filter(item => item.name === name)
        if (target.length) return target[0].id
        return null
      } else {
        const data = []
        list.map(item => {
          if (item.data) {
            item.data.forEach(item => {
              data.push(item)
            })
          }
        })
        return this.findCloudproviderId(data, name, idx - 1)
      }
    },
    handleFooterCellClick (params) {
      const { column } = params
      if (!this.isGoPage) return
      let { property } = column
      if (property.includes('->')) {
        property = property.split('->')[0]
      }
      if (['percent'].includes(property) || property.startsWith('name')) return
      const ret = {}
      // 非维度列和总计占比列的数据列，使用数据列的时间
      if (!(['percent'].includes(column.property) || ['all'].includes(column.property) || column.property.startsWith('name'))) {
        const { data_type } = this.formData.date
        if (data_type === 'day') {
          ret.start_date = this.$moment(property).format('YYYY-MM-DD') + 'TZ'
          ret.end_date = ret.start_date
        } else {
          ret.start_date = this.$moment(property).startOf('month').format('YYYY-MM-DD') + 'TZ'
          ret.end_date = this.$moment(property).endOf('month').format('YYYY-MM-DD') + 'TZ'
        }
      }
      this.$emit('goBillPage', ret)
    },
    rowspanMethod ({ row, rowIndex, column, visibleData }) {
      const fields = ['name1', 'name2', 'name3', 'name4']
      if (this.dataGroup.length > 1 && fields.includes(column.property)) {
        const prevRow = visibleData[rowIndex - 1]
        let nextRow = visibleData[rowIndex + 1]
        if (prevRow && this.needMerge(prevRow, row, column.property)) {
          return { rowspan: 0, colspan: 0 }
        } else {
          let countRowspan = 1
          while (nextRow && this.needMerge(nextRow, row, column.property)) {
            nextRow = visibleData[++countRowspan + rowIndex]
          }
          if (countRowspan > 1) {
            return { rowspan: countRowspan, colspan: 1 }
          }
        }
      }
    },
    needMerge (row1, row2, property) {
      const index = property.replace('name', '')
      const list = Array(Number(index)).fill().map((_, i) => `name${i + 1}`)
      return list.every(field => row1[field] === row2[field])
    },
    cellClassName ({ column }) {
      if (!this.isGoPage) return ''
      if (!(['percent'].includes(column.property) || column.property.startsWith('name'))) {
        return 'hover-change-color-cell'
      }
    },
    cellStyle ({ row, rowIndex, column }) {
      const ret = {}
      if (!this.isGoPage) return ret
      if (row.rowType === 'total') {
        ret.color = 'black'
      }
      if (!(['percent'].includes(column.property) || column.property.startsWith('name'))) {
        ret.cursor = 'pointer'
      }
      return ret
    },
    footerCellStyle ({ row, rowIndex, column }) {
      const ret = {
        color: 'black',
      }
      if (column.property === 'name1') {
        ret.textAlign = 'right'
        if (this.groupByLen > 1) {
          ret.backgroundImage = 'none'
        }
      } else if (['name2', 'name3', 'name4', 'name5'].includes(column.property)) {
        ret.color = 'transparent'
      } else if (column.property !== 'percent') {
        ret.backgroundSize = '1px 100%, 100% 0'
        if (this.isGoPage) {
          ret.cursor = 'pointer'
        }
      }
      return ret
    },
    sortMethod ({ data, column, property, order }) {
      let billType = this.billTypes[0]
      let pro = property
      if (property.includes('->')) {
        pro = property.split('->')[0]
        billType = property.split('->')[1]
      }
      const sortData = this.getSortData(this.tableData.data, pro, order, billType)
      const { currentPage, pageSize } = this.tablePage
      return sortData.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    },
    download () {
      const data = this.exportParams.data
      const filename = `${this.$t('bill.text_118')}.xlsx`
      const ws_name = 'Sheet1'
      const wb = XLSX.utils.book_new()
      const cols = Array(this.dataGroup.length).fill().map((_, i) => i)
      const t = []
      data.forEach((_, rowIdx) => {
        cols.forEach((_, colIdx) => {
          t.push({ row: rowIdx, col: colIdx, value: 's' }) // 格式化为文本
        })
      })
      const ws = addDataToSheetAfterFormat({ data, ignoreColsIndex: cols, t })
      const merges = this.exportParams.merges.filter(item => item.s.r !== item.e.r || item.s.c !== item.e.c)
      // 过滤和验证合并单元格
      const validMerges = merges.filter((item, index, self) => {
        // 确保起始位置不等于结束位置
        if (item.s.r === item.e.r && item.s.c === item.e.c) {
          return false
        }
        // 确保起始位置小于等于结束位置
        if (item.s.r > item.e.r || item.s.c > item.e.c) {
          return false
        }
        // 确保合并范围不超出数据范围
        const maxRow = data.length - 1
        const maxCol = data[0] ? data[0].length - 1 : 0
        if (item.s.r < 0 || item.e.r > maxRow || item.s.c < 0 || item.e.c > maxCol) {
          return false
        }
        const otherIdx = self.findIndex(merge =>
          merge.s.r === item.s.r &&
          merge.s.c === item.s.c &&
          merge.e.r === item.e.r &&
          merge.e.c === item.e.c)
        if (otherIdx !== index) {
          return false
        }
        return true
      })
      ws['!merges'] = validMerges
      XLSX.utils.book_append_sheet(wb, ws, ws_name)
      XLSX.writeFile(wb, filename)
    },
  },
}
</script>
<style lang="less" scoped>
.vxe-pager--num-btn .is--active {
  color: var(--antd-wave-shadow-color)!important;
}
</style>
